/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TimezoneProps {
  id: string;
  name: string;
  abbreviation: string;
  code: string;
  isDst: boolean;
  utcOffset: number;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TimezoneRecord extends Record<TimezoneProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  code: null as any,
  isDst: null as any,
  utcOffset: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    code: string;
    isDst: boolean;
    utcOffset: number;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TimezoneRecord {
    if (json == null) {
      throw new Error('TimezoneRecord.fromJS: parameter cannot be null');
    }
    return new TimezoneRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TimezoneJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'TimezoneJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'TimezoneJson.abbreviation'),
      code: getFromOrThrow(json, (j) => j.code, 'TimezoneJson.code'),
      isDst: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isDst', false),
        'TimezoneJson.isDst',
      ),
      utcOffset: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'utcOffset', 0),
        'TimezoneJson.utcOffset',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    code: string;
    isDst: boolean;
    utcOffset: number;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TimezoneRecord, Error[]] {
    if (json == null) {
      throw new Error('TimezoneRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TimezoneRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TimezoneJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'TimezoneJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'TimezoneJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      code: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.code, 'TimezoneJson.code'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isDst: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => propValueOrDefault(j, 'isDst', false), 'TimezoneJson.isDst'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      utcOffset: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'utcOffset', 0),
            'TimezoneJson.utcOffset',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TimezoneProps) {
    super(props);
    if (props == null) {
      throw new Error('TimezoneRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TimezoneViewModelBase implements TimezoneProps {
  protected readonly _props: TimezoneProps;
  private _json?: TimezoneProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get code(): string {
    return this._props.code;
  }
  get isDst(): boolean {
    return this._props.isDst;
  }
  get utcOffset(): number {
    return this._props.utcOffset;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: TimezoneProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        code: this._props.code,
        isDst: this._props.isDst,
        utcOffset: this._props.utcOffset,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTimezoneEntities = (state: AppState) => state.entityStores.timezone.entities;

export const selectTimezoneById = (id: string) => (state: AppState) =>
  state.entityStores.timezone.entities.get(id);

export const selectTimezoneMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.timezone.meta.get(id);

export const selectTimezoneAndMetadataById = (id: string) =>
  createSelector(selectTimezoneById(id), selectTimezoneMetadataById(id), (timezone, meta) => ({
    timezone,
    meta,
  }));

export function selectTimezoneEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTimezoneEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TimezoneRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTimezoneEntitiesByName(name: string) {
  return createSelector(selectAllTimezoneEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectTimezoneEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllTimezoneEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectTimezoneEntitiesByCode(code: string) {
  return createSelector(selectAllTimezoneEntities, (entities) =>
    entities.filter((e) => e.code === code).valueSeq(),
  );
}

export function selectTimezoneEntitiesByIsDst(isDst: boolean) {
  return createSelector(selectAllTimezoneEntities, (entities) =>
    entities.filter((e) => e.isDst === isDst).valueSeq(),
  );
}

export function selectTimezoneEntitiesByUtcOffset(utcOffset: number) {
  return createSelector(selectAllTimezoneEntities, (entities) =>
    entities.filter((e) => e.utcOffset === utcOffset).valueSeq(),
  );
}
//#endregion Selectors
